<template>
  <div ref="promoMsgContainer" :class="{ sticky: isSticky }" class="fixed-promo-message-container">
    <template v-if="promoMessageItems?.length">
      <message-item
        v-for="(fixedPromoMessageItem, index) in promoMessageItems"
        :key="`fixed-promo-message-${index}`"
        class="fixed-promo-message--desktop"
        :fields="fixedPromoMessageItem"
        @close-msg="closePromoMsg($event)"
      />
    </template>
    <message-item
      v-if="mobileMessageItem"
      class="fixed-promo-message--mobile"
      :fields="mobileMessageItem"
      @close-msg="closePromoMsg($event)"
    />
  </div>
</template>

<script setup lang="ts">
import {
  onMounted,
  onBeforeUnmount,
  onUpdated,
  ref,
  computed,
  nextTick,
  useTemplateRef,
} from 'vue';
import { useThrottleFn } from '@vueuse/core';
import { useCookies } from '@vueuse/integrations/useCookies';
import { isServer, isEditorActive } from '@sitecore-jss/sitecore-jss/utils';
import MessageItem from './FixedPromoMessageItem/FixedPromoMessageItem.vue';
import { useAppStore } from '../../stores/appStore';
import type { FixedPromoMessageProps, FixedPromoMessageFields } from './types';

const props = withDefaults(defineProps<FixedPromoMessageProps>(), {
  fields: () => ({}) as FixedPromoMessageFields,
  params: () => ({}),
});

const cookies = !isServer() && useCookies();
const appStore = useAppStore();

const promoMsgContainer = useTemplateRef('promoMsgContainer');
const promoMsgOffset = ref(0);
const isSticky = ref(false);
const isStickyEnabled = props.fields?.isStickyEnabled?.value;

const promoMessageItems = computed(
  () =>
    props.fields?.promoMessages?.value?.map((item) => ({
      ...item,
      isVisible: cookies ? !cookies.get(`promoMsgClosed_${item.id}`) : true,
    }))
);

const mobileMessageItem = computed(() =>
  props.fields?.mobilePromoMessage?.value?.id
    ? {
        ...props.fields.mobilePromoMessage.value,
        isVisible: cookies
          ? !cookies.get(`promoMsgClosed_${props.fields.mobilePromoMessage.value.id}`)
          : true,
      }
    : null
);

const calculatePromoMsgContainerHeight = useThrottleFn(() => {
  if (promoMsgContainer.value && !isEditorActive() && isStickyEnabled) {
    promoMsgOffset.value = promoMsgContainer.value.offsetHeight;
    appStore.setBodyOffset(promoMsgOffset.value);
    isSticky.value = true;
  }
}, 50);

const closePromoMsg = ({ id }: { id: string }) => {
  if (!cookies) return;
  cookies.set(`promoMsgClosed_${id}`, true);

  nextTick(() => {
    calculatePromoMsgContainerHeight();
  });
};

onMounted(() => {
  if (!isEditorActive() && isStickyEnabled) {
    window.addEventListener('resize', onWindowResize);
    calculatePromoMsgContainerHeight();
  }
});

onUpdated(() => {
  calculatePromoMsgContainerHeight();
});

onBeforeUnmount(() => {
  if (!isEditorActive() && isStickyEnabled) {
    window.removeEventListener('resize', onWindowResize);
    appStore.setBodyOffset(0);
  }
});

const onWindowResize = () => {
  (async () => {
    await calculatePromoMsgContainerHeight();
  })();
};
</script>

<style lang="scss">
@import './scss/FixedPromoMessage.scss';
</style>
